import { useEffect, useState, useMemo, useRef } from 'react';
import { IconButton, Tooltip } from '@material-ui/core';
import PopupRef from '../../../component/PopupRef';
import CodeGroupModal from '../../modal/CodeGroupModal';
import CodeModal from '../../modal/CodeModal';
import { modalType } from '../../../util/ModalUtil';
import RefGridTable from '../../../component/grid/RefGridTable';
import BreadCrumb from '../../../component/BreadCrumb';
import ModalRef from '../../../component/ModalRef';
import DeleteIcon from '../../../component/icon/DeleteIcon';
import RegistButtonBox from '../../../component/buttons/RegistButtonBox';
import { useDispatch, useSelector } from 'react-redux';
import {
    actionCodeGroupRemove,
    actionCodeGroupSearch,
    actionCodeRemove,
    actionCodeSearch,
    actionInit,
} from '../../../module/body/system/SystemCommonCodeModule';
import { VIEW_SYSTEM_COMMON_CODE } from './../../../constant/ViewConstant';
import { useLocation } from 'react-router-dom';
import PathUtil from '../../../util/PathUtil';
import qs from 'qs';
import { OpenEditIcon } from '../../../component/icon/GridIcons';

const SystemCommonCodeView = () => {
    const location = useLocation();
    const queryData = qs.parse(location.search, { ignoreQueryPrefix: true });

    const dispatch = useDispatch();
    const { codeGroups, codes } = useSelector((state) => state.systemCommonCodeState);

    const [cdGrpId, setCdGrpId] = useState(null);
    const [cdGrpNm, setCdGrpNm] = useState(null);

    const codeGroupGridTableRef = useRef(null);
    const codeGridTableRef = useRef(null);

    const modalRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {
        dispatch(actionInit());

        queryData.cdGrpId && setCdGrpId(queryData.cdGrpId);
        queryData.cdGrpNm && setCdGrpNm(queryData.cdGrpNm);

        codeGroupGridTableRef.current.handleDoubleClick(() => {
            return codeGrpRowDoubleClick;
        });
        codeGroupGridTableRef.current.handleClick(() => {
            return codeGrpRowClick;
        });

        dispatch(actionCodeGroupSearch());

        return () => {
            dispatch(actionInit());
        };
    }, []);

    const codeGrpRowDoubleClick = (params) => {
        modalRef.current.open(<CodeGroupModal type={modalType.EDIT} row={params.row} modalRef={modalRef} />);
    };

    const codeGrpRowClick = (params) => {
        setCdGrpId(params.row.cdGrpId);
        setCdGrpNm(params.row.cdGrpNm);
        PathUtil.pushState(location.pathname, { cdGrpId: params.row.cdGrpId, cdGrpNm: params.row.cdGrpNm });
    };

    // 최초 코드그룹이 조회되어 코드그룹데이터가 변경되면 호출
    useEffect(() => {
        codeGroups && codeGroupGridTableRef.current.handleRows(codeGroups, codeGroups.length, 0, 10);
    }, [codeGroups]);

    // 코드가 더블클릭 재설정
    useEffect(() => {
        codeGridTableRef.current.handleDoubleClick(() => {
            return codeRowDoubleClick;
        });
    }, [cdGrpId, cdGrpNm]);

    const codeRowDoubleClick = (params) => {
        modalRef.current.open(
            <CodeModal
                type={modalType.EDIT}
                cdGrpId={cdGrpId}
                cdGrpNm={cdGrpNm}
                row={params.row}
                modalRef={modalRef}
            />,
        );
    };

    // 코드그룹이 클릭되면 호출
    useEffect(() => {
        if (cdGrpId != null) {
            dispatch(actionCodeSearch({ cdGrpId }));
        }
    }, [cdGrpId]);

    // 코드그룹이 선택되어 코드데이터가 변경되면 호출
    useEffect(() => {
        codes && codeGridTableRef.current.handleRows(codes, codes.length, 0, 10);
    }, [codes]);

    // 코드그룹 그리드테이블에서 삭제기능 컬럼을 위한 함수
    const handleDeleteCodeGroup = (row) => {
        popupRef.current.openWarning(
            <>
                코드그룹: <em>{row.cdGrpId}</em>
                <br />
                코드그룹명: <em>{row.cdGrpNm}</em>
                <br />
                삭제하시겠습니까?
            </>,
            () => {
                return function () {
                    dispatch(actionCodeGroupRemove({ cdGrpId: row.cdGrpId }));
                    setCdGrpId(null);
                    setCdGrpNm(null);
                    PathUtil.pushState(location.pathname);
                };
            },
        );
    };

    // 코드 그리드테이블에서 삭제기능 컬럼을 위한 함수
    const handleDeleteCode = (row) => {
        popupRef.current.openWarning(
            <>
                코드: <em>{row.cd}</em>
                <br />
                코드명: <em>{row.cdNm}</em>
                <br />
                삭제하시겠습니까?
            </>,
            () => {
                return function () {
                    dispatch(actionCodeRemove({ cdGrpId: row.cdGrpId, cd: row.cd }));
                };
            },
        );
    };

    // 코드그룹 등록버튼 처리
    const handleAddCodeGroup = () => {
        // codeGroupModalRef.current.clear();
        modalRef.current.open(<CodeGroupModal type={modalType.ADD} modalRef={modalRef} />);
    };

    // 코드 등록버튼 처리
    const handleAddCode = (cdGrpId, cdGrpNm) => {
        if (cdGrpId == null || cdGrpId == undefined) {
            popupRef.current.openAlert(<>코드그룹을 먼저 선택해주세요.</>);
        } else {
            // codeModalRef.current.clear();
            modalRef.current.open(
                <CodeModal type={modalType.ADD} cdGrpId={cdGrpId} cdGrpNm={cdGrpNm} modalRef={modalRef} />,
            );
        }
    };

    // 참고) useMemo는 렌더링시 변수를 재선언하지 않고 재활용하기 위해 사용
    const codeGroupColumns = useMemo(
        () => [
            {
                field: 'cdGrpId',
                headerName: '코드그룹',
                headerAlign: 'center',
                align: 'left',
                tooltip: true,
                flex: 10,
            },
            {
                field: 'cdGrpNm',
                headerName: '코드그룹명',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
            },
            {
                field: 'detail',
                headerName: '기능',
                headerAlign: 'center',
                align: 'center',
                flex: 6,
                disableColumnMenu: true,
                hideSortIcons: true,
                disableExport: true,
                renderCell: (params) => (
                    <>
                        <Tooltip title='수정'>
                            <IconButton onClick={(e) => codeGrpRowDoubleClick(params)}>
                                <OpenEditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='삭제'>
                            <IconButton onClick={(e) => handleDeleteCodeGroup(params.row)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
        [codeGroups],
    );

    // 참고) useMemo는 렌더링시 변수를 재선언하지 않고 재활용하기 위해 사용
    const codeColumns = useMemo(
        () => [
            {
                field: 'cd',
                headerName: '코드',
                headerAlign: 'center',
                align: 'left',
                tooltip: true,
                flex: 10,
                renderCell: (params) =>
                    params.row.useStatusCd != 'ENABLED' ? (
                        <del style={{ color: '#FF6C6C' }}>{params.row.cd}</del>
                    ) : (
                        <div>{params.row.cd}</div>
                    ),
            },
            {
                field: 'cdNm',
                headerName: '코드명',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) =>
                    params.row.useStatusCd != 'ENABLED' ? (
                        <del style={{ color: '#FF6C6C' }}>{params.row.cdNm}</del>
                    ) : (
                        <div>{params.row.cdNm}</div>
                    ),
            },
            {
                field: 'useStatusCd',
                headerName: '사용여부',
                headerAlign: 'center',
                align: 'center',
                flex: 5,
                renderCell: (params) =>
                    params.row.useStatusCd == 'ENABLED' ? (
                        <div>사용</div>
                    ) : (
                        <div style={{ color: '#FF6C6C' }}>미사용</div>
                    ),
            },
            {
                field: 'detail',
                headerName: '기능',
                headerAlign: 'center',
                align: 'center',
                flex: 7,
                disableColumnMenu: true,
                hideSortIcons: true,
                disableExport: true,
                renderCell: (params) => (
                    <>
                        <Tooltip title='수정'>
                            <IconButton onClick={(e) => codeRowDoubleClick(params)}>
                                <OpenEditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='삭제'>
                            <IconButton onClick={(e) => handleDeleteCode(params.row)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
        [codeGroups, codes],
    );

    return (
        <section className='container'>
            <div className='container-inner'>
                <div className='con-title-wrap conbg02'>
                    {/* <h2 className='con-title'>공통코드관리</h2> */}
                    <h2 className='con-title'>{VIEW_SYSTEM_COMMON_CODE.title}</h2>
                    <p className='con-title-p'>{VIEW_SYSTEM_COMMON_CODE.subTitle}</p>
                </div>
                <div className='sub-nav-wrap'>
                    <BreadCrumb />
                </div>
                <div className='commonCode-wrap clearfix'>
                    <div className='left-box'>
                        <div className='con-tb-title'>
                            <h3 className='tb-tit-name'>코드그룹 목록</h3>
                            <RegistButtonBox onClick={(e) => handleAddCodeGroup()} />
                        </div>
                        <div className='con-tb-box table-common'>
                            <RefGridTable columns={codeGroupColumns} hideHeader ref={codeGroupGridTableRef} />
                        </div>
                    </div>
                    <div className='right-box'>
                        <div className='con-tb-title'>
                            <h3 className='tb-tit-name'>코드 목록 {cdGrpNm && `(${cdGrpNm})`}</h3>
                            <RegistButtonBox onClick={(e) => handleAddCode(cdGrpId, cdGrpNm)} />
                        </div>
                        <div className='con-tb-box table-common'>
                            <RefGridTable columns={codeColumns} hideHeader ref={codeGridTableRef} />
                        </div>
                    </div>
                </div>
            </div>
            <PopupRef ref={popupRef} />
            <ModalRef ref={modalRef} />
        </section>
    );
};

export default SystemCommonCodeView;
